import dayjs, { Dayjs } from "dayjs";
import apiService, {
  GetRefundsRequest,
  parseGetRefundsResponse,
} from "../../services/api";
import { formatMobileNumber } from "../../utils/formatters";
import {
  formatRefundDateTime,
  formatRefundRideEndDate,
  formatRefundRideStartDate,
  pickRefundRecordScalar,
  REFUND_RIDE_END_DATE_KEYS,
  REFUND_RIDE_START_DATE_KEYS,
} from "./refundDateFormat";

type CodeOption = {
  codevalue?: string | number;
  codedisplayname?: string;
};

interface RefundAuditEntry {
  auditid?: number;
  description?: string;
  userid?: number;
  enteredon?: string;
  planname?: string;
}

const getScalar = (item: Record<string, unknown>, keys: string[]): unknown => {
  for (const k of keys) {
    if (k in item && item[k] != null && item[k] !== "") return item[k];
  }
  return undefined;
};

const formatDurationValue = (value: unknown): string => {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.trim();
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value);
};

function getRefundAuditList(r: Record<string, unknown>): RefundAuditEntry[] {
  const raw = r.auditlist;
  if (!Array.isArray(raw)) return [];
  return raw as RefundAuditEntry[];
}

function getLatestAudit(r: Record<string, unknown>): RefundAuditEntry | null {
  const list = getRefundAuditList(r);
  if (list.length === 0) return null;
  return [...list].sort(
    (a, b) =>
      dayjs(b.enteredon ?? 0).valueOf() - dayjs(a.enteredon ?? 0).valueOf(),
  )[0];
}

function isTruthyFlag(v: unknown): boolean {
  return (
    v === true ||
    v === 1 ||
    (typeof v === "string" && v.trim().toLowerCase() === "true")
  );
}

function getProcessedBy(r: Record<string, unknown>): string {
  if (isTruthyFlag(r.isaddedbyWeb ?? r.isaddedbyweb ?? r.IsAddedByWeb)) {
    return "Web";
  }
  const latest = getLatestAudit(r);
  if (latest?.userid != null && Number.isFinite(Number(latest.userid))) {
    return `User ID ${latest.userid}`;
  }
  return "";
}

function getProcessedAt(r: Record<string, unknown>): string {
  const initiated = getScalar(r, ["refundinitiatedon", "refundInitiatedOn"]);
  if (initiated != null && String(initiated).trim() !== "") {
    return formatRefundDateTime(String(initiated));
  }
  const latest = getLatestAudit(r);
  if (latest?.enteredon) {
    return formatRefundDateTime(latest.enteredon);
  }
  return "";
}

function resolveStatusLabel(code: string, options: CodeOption[]): string {
  const c = code.trim();
  if (!c) return "";
  const opt = options.find((item) => String(item.codevalue ?? "") === c);
  return opt?.codedisplayname ? String(opt.codedisplayname) : c;
}

function resolvePaymentMethodLabel(
  code: unknown,
  labels: Record<string, string>,
): string {
  const key = String(code ?? "").trim();
  if (!key) return "";
  return labels[key] ?? key;
}

function isDateInRange(
  iso: string,
  from: Dayjs | null,
  to: Dayjs | null,
): boolean {
  const d = dayjs(iso);
  if (!d.isValid()) return false;
  if (from && d.isBefore(from.startOf("day"))) return false;
  if (to && d.isAfter(to.endOf("day"))) return false;
  return true;
}

/** Client-side filter by ride start and/or ride completion timestamps. */
export function filterRefundItemsByRideDateRange(
  items: unknown[],
  from: Dayjs | null,
  to: Dayjs | null,
): unknown[] {
  if (!from && !to) return items;
  return items.filter((item) => {
    const r =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    const candidates = [
      pickRefundRecordScalar(r, REFUND_RIDE_START_DATE_KEYS),
      pickRefundRecordScalar(r, REFUND_RIDE_END_DATE_KEYS),
    ];
    return candidates.some((d) => {
      if (d == null || String(d).trim() === "") return false;
      return isDateInRange(String(d), from, to);
    });
  });
}

export async function fetchRefundsForExport(
  body: GetRefundsRequest,
  rideFrom: Dayjs | null,
  rideTo: Dayjs | null,
): Promise<unknown[]> {
  const hasRideRange = Boolean(rideFrom || rideTo);
  const response = await apiService.getRefunds({
    ...body,
    pageNumber: 1,
    pageSize: 10000,
    /** Broader fetch when ride range is set; narrowed client-side below. */
    ridestartdate: hasRideRange ? null : body.ridestartdate,
    ridecompletiondate: hasRideRange ? null : body.ridecompletiondate,
  });
  const { items } = parseGetRefundsResponse(response);
  return filterRefundItemsByRideDateRange(items, rideFrom, rideTo);
}

export function transformRefundsForCsvExport(
  items: unknown[],
  refundStatusOptions: CodeOption[],
  paymentMethodLabels: Record<string, string>,
): Record<string, string | number>[] {
  return items.map((item, idx) => {
    const r =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {};

    const statusCode = String(
      getScalar(r, [
        "refundstatus",
        "refundStatus",
        "paymentstatus",
        "paymentStatus",
      ]) ?? "",
    );
    const phone = getScalar(r, ["phonenumber", "phoneNumber"]);
    const latestAudit = getLatestAudit(r);

    return {
      "Refund ID": String(getScalar(r, ["refundid", "refundId"]) ?? idx),
      "Booking ID": String(
        getScalar(r, ["bookingmasterid", "bookingMasterId"]) ?? "",
      ),
      "Customer Name": String(getScalar(r, ["name", "userName"]) ?? ""),
      "Mobile Number":
        typeof phone === "string" || typeof phone === "number"
          ? formatMobileNumber(phone) || String(phone)
          : "",
      Hub: String(getScalar(r, ["hubname", "hubName"]) ?? ""),
      Vehicle: String(
        getScalar(r, [
          "vehiclenum",
          "vehicleNum",
          "VehicleNum",
          "vehiclename",
          "vehicleName",
          "VehicleName",
        ]) ?? "",
      ),
      "Ride Start Date": formatRefundDateTime(
        String(
          getScalar(r, [
            "ridestartdate",
            "rideStartDate",
            "RideStartDate",
            "bookingstartdate",
            "bookingStartDate",
          ]) ?? "",
        ),
      ),
      Duration: formatDurationValue(
        getScalar(r, [
          "duration",
          "Duration",
          "tripduration",
          "tripDuration",
          "rentedduration",
          "rentedDuration",
        ]),
      ),
      "Booking Amount": Number(
        getScalar(r, ["bookingamount", "bookingAmount"]) ?? 0,
      ),
      "Proposed Refundable Amount": Number(
        getScalar(r, ["refundableamount", "refundableAmount"]) ?? 0,
      ),
      "Final Refund Amount": Number(
        getScalar(r, ["proposedamount", "proposedAmount"]) ?? 0,
      ),
      "Refund Status": resolveStatusLabel(statusCode, refundStatusOptions),
      "Payment Method": resolvePaymentMethodLabel(
        getScalar(r, ["paymentmethod", "paymentMethod"]),
        paymentMethodLabels,
      ),
      "Transaction ID": String(
        getScalar(r, ["transactionid", "transactionId"]) ?? "",
      ),
      "Razorpay Refund ID": String(
        getScalar(r, ["razorpayrefundid", "razorpayRefundId"]) ?? "",
      ),
      "Ride Completion Date": formatRefundRideEndDate(r),
      "Refund Initiated On": formatRefundDateTime(
        String(getScalar(r, ["refundinitiatedon", "refundInitiatedOn"]) ?? ""),
      ),
      "Refund Type": String(getScalar(r, ["refundtype", "refundType"]) ?? ""),
      "Refund Description": String(
        getScalar(r, ["refunddescription", "refundDescription"]) ?? "",
      ),
      "Update Reason": String(
        getScalar(r, ["updatereason", "updateReason"]) ?? "",
      ),
      "Processed By": getProcessedBy(r),
      "Processed At": getProcessedAt(r),
      Plan: String(latestAudit?.planname ?? ""),
      "Latest Audit Note": String(latestAudit?.description ?? ""),
    };
  });
}

export function buildRefundsExportFilename(
  exportFrom: Dayjs | null,
  exportTo: Dayjs | null,
): string {
  const from = exportFrom?.format("YYYY-MM-DD") ?? "all";
  const to = exportTo?.format("YYYY-MM-DD") ?? "all";
  return `refunds-export_${from}_to_${to}.csv`;
}
